import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState, useEffect } from 'react';

interface RefreshBannerProps {
  onRefresh: () => void;
  onIgnore: () => void;
}

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  banner: {
    position: 'absolute',
    top: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'fit-content',
    background: '#02263d',
    color: '#fff',
    paddingTop: spacing(1.5),
    paddingBottom: spacing(1.5),
    paddingLeft: '20px',
    paddingRight: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing(1.5),
    zIndex: 9999,
  },
  refreshButton: {
    background: '#FFC107',
    border: 'none',
    padding: `${spacing(1)} ${spacing(1.5)}`,
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.2s, box-shadow 0.2s',
    color: 'rgba(0,0,0,0.87)',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    '&:hover:not(:disabled)': {
      background: '#FFD54F',
      transform: 'scale(1.05)',
      boxShadow: '0 4px 8px rgba(255, 193, 7, 0.4)',
    },
    '&:active:not(:disabled)': {
      background: '#FFB300',
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
  ignoreButton: {
    background: '#FF0048',
    border: 'none',
    padding: `${spacing(1)} ${spacing(1.5)}`,
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.2s, box-shadow 0.2s',
    color: '#fff',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    '&:hover:not(:disabled)': {
      background: '#FF3366',
      transform: 'scale(1.05)',
      boxShadow: '0 4px 8px rgba(255, 0, 72, 0.4)',
    },
    '&:active:not(:disabled)': {
      background: '#CC0036',
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
}));

const RefreshBanner = ({ onRefresh, onIgnore }: RefreshBannerProps) => {
  const { banner, refreshButton, ignoreButton } = useStyles();
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isDisabled) {
      timer = setTimeout(() => {
        setIsDisabled(false);
      }, 5000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isDisabled]);

  const handleRefresh = () => {
    if (!isDisabled) {
      setIsDisabled(true);
      onRefresh();
    }
  };

  const handleIgnore = () => {
    if (!isDisabled) {
      setIsDisabled(true);
      onIgnore();
    }
  };

  return (
    <div className={banner}>
      <span>🚀 A new HITL version is available</span>
      <button
        onClick={handleRefresh}
        className={refreshButton}
        disabled={isDisabled}
      >
        Refresh
      </button>
      <button
        onClick={handleIgnore}
        className={ignoreButton}
        disabled={isDisabled}
      >
        Ignore
      </button>
    </div>
  );
};

export default RefreshBanner;
